import { Inject, inject, Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DeviceSize } from '../../model/device-size';
import { DeviceCapabilities } from '../device-service/device-capabilities';
import { DevicePointer } from '../device-service/device-pointer';
import { DevicePreferences } from '../device-service/device-preferences';
import { WINDOW } from '../window.service';

const breakpoints: { [key in DeviceSize]: number } = {
  [DeviceSize.MIN_WIDTH]: 0,
  [DeviceSize.APP_MIN_WIDTH]: 320,
  [DeviceSize.XXS]: 380,
  [DeviceSize.XS]: 520,
  [DeviceSize.S]: 768,
  [DeviceSize.M]: 1024,
  [DeviceSize.BOARD_TABS_MOBILE_BREAKPOINT]: 1234,
  [DeviceSize.L]: 1280,
  [DeviceSize.Xl]: 1512,
  [DeviceSize.APP_MAX_WIDTH]: 1920,
  [DeviceSize.MAX_WIDTH]: Number.MAX_SAFE_INTEGER,
};

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  smallerThanSmallDeviceList: DeviceSize[];
  smallerThanMediumDeviceList: DeviceSize[];

  private _capabilities: DeviceCapabilities;
  private _detector: DeviceDetectorService;

  get capabilities(): DeviceCapabilities {
    return this._detectCapabilities();
  }

  get deviceSize(): DeviceSize {
    let previousKey: DeviceSize | undefined = undefined;
    for (const [key, value] of Object.entries(breakpoints)) {
      if (!this.matchesSize(value)) {
        if (previousKey) {
          return previousKey;
        } else {
          return DeviceSize.MAX_WIDTH;
        }
      }
      previousKey = key as DeviceSize;
    }
    return previousKey ?? DeviceSize.MAX_WIDTH;
  }

  /**
   * This is only accurate at the current moment and might change after a user resize.
   */
  get isSmallerThanSmall(): boolean {
    // Returns everything BELOW S
    return this.smallerThanSmallDeviceList.includes(this.deviceSize);
  }

  get isSmallerThanMedium(): boolean {
    // Returns everything BELOW L
    return this.smallerThanMediumDeviceList.includes(this.deviceSize);
  }

  get hasHoverCapability(): boolean {
    return this._capabilities.hover;
  }

  get hasLimitedAnimationCapabilities(): boolean {
    return this.isProbablyAMobileDevice();
  }

  get preferences(): DevicePreferences {
    return this._detectPreferences();
  }

  constructor(@Inject(WINDOW) private window: Window) {
    this._capabilities = this._detectCapabilities();
    this._detector = inject(DeviceDetectorService);

    const deviceSizes = Object.values(DeviceSize);
    const smallSize = deviceSizes.indexOf(DeviceSize.S);
    // Returns everything BELOW S
    this.smallerThanSmallDeviceList = deviceSizes.slice(0, smallSize);
    const mediumSize = deviceSizes.indexOf(DeviceSize.M);
    // Returns everything BELOW L
    this.smallerThanMediumDeviceList = deviceSizes.slice(0, mediumSize);
  }

  private _detectCapabilities(): DeviceCapabilities {
    return {
      availablePointers: this._detectPointers(),
      hover: this._detectHoverCapability(),
      primaryPointer: this._detectPrimaryPointer(),
    };
  }

  private _detectHoverCapability(): boolean {
    const reportsHover = this._matchMedia('(hover: hover)');
    const reportsHoverNone = this._matchMedia('(hover: none)');
    const canBeTrustedToSupportRule = reportsHover !== reportsHoverNone;
    if (canBeTrustedToSupportRule) {
      return reportsHover;
    }
    return !this.isProbablyAMobileDevice();
  }

  private _detectPreferences(): DevicePreferences {
    return {
      prefersReducedMotion: this._detectPrefersReducedMotion(),
    };
  }

  private _detectPrefersReducedMotion(): boolean {
    return this._matchMedia('(prefers-reduced-motion)');
  }

  private _detectPointers(): Array<DevicePointer.Coarse | DevicePointer.Fine> {
    const pointers: Array<DevicePointer.Coarse | DevicePointer.Fine> = [];
    if (this._matchMedia('(any-pointer:fine)')) {
      pointers.push(DevicePointer.Fine);
    }
    if (this._matchMedia('(any-pointer:coarse)')) {
      pointers.push(DevicePointer.Coarse);
    }
    return pointers;
  }

  private _detectPrimaryPointer(): DevicePointer {
    if (this._matchMedia('(pointer:fine)')) {
      return DevicePointer.Fine;
    }
    if (this._matchMedia('(pointer:coarse)')) {
      return DevicePointer.Coarse;
    }
    return DevicePointer.None;
  }

  private matchesSize(size: number): boolean {
    return this._matchMedia(`(min-width: ${size}px)`);
  }

  private _matchMedia(media: string): boolean {
    return this.window.matchMedia(media).matches;
  }

  /**
   * Does a best guess if feature detection can't be used. Avoid using this when possible.
   */
  private isProbablyAMobileDevice(): boolean {
    return !!this._detector.isMobile(this.window.navigator.userAgent);
  }
}
